import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, Settings, Users, Plus, Search, Edit2, Clock, Trash2, MoreHorizontal, Copy, Clipboard, RefreshCw, ChevronLeft, FileDown, LayoutPanelLeft, ArrowLeft, Calendar as CalendarIcon, Wallet, Map as MapIcon, Filter, Check, FileUp } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO, addDays, startOfWeek, subWeeks, addWeeks, isSameDay, isBefore, getISOWeek, getYear, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

import { Planning, PlanningRow, Shift, Template, ExtraShift, ShiftSegment, ShiftType, ShiftServiceType, ABSENCE_TYPES } from './types';
import ShiftCell from './ShiftCell';
import ShiftEditor from './ShiftEditor';
import AddShiftModal from './AddShiftModal';
import PlanningSettings from './PlanningSettings';
import { PlanningExportModal } from './PlanningExportModal';
import { ImportExcelModal } from './import/ImportExcelModal';
import { useUndo } from '../ui/UndoBar';
import { ConfirmModal } from '../ui/ConfirmModal';
import type { ConfirmRequest } from '../ui/ConfirmModal';

import { planningApi, moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { InfoModal } from '../InfoModal';
import { generatePlanningGridPDF } from './pdfExportUtils';
import { getShiftCounts as getShiftCountsShared } from './shiftServiceUtils';
import { PrestationEmployeePlanning } from '../Employee/PrestationEmployeePlanning';
import { AmbianceBand } from '../Ambiance/AmbianceBand';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/**
 * La semaine affichée est inscrite dans l'adresse (?week=YYYY-MM-DD) : c'est elle qui permet de
 * rafraîchir la page ou d'utiliser le bouton « précédent » du navigateur sans perdre la semaine
 * consultée. On recale toujours sur le lundi, pour qu'une adresse bricolée à la main tombe juste.
 */
const parseWeekParam = (raw: string | null): Date | null => {
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const parsed = parseISO(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return startOfWeek(parsed, { weekStartsOn: 1 });
};

const PlanningModuleShiftGrid = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
        () => parseWeekParam(new URLSearchParams(window.location.search).get('week')) ?? startOfWeek(new Date(), { weekStartsOn: 1 })
    );
    const [planning, setPlanning] = useState<Planning | null>(null);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [absenceTypes, setAbsenceTypes] = useState<{ code: string; isFullDay: boolean; autoApply: boolean; color?: string }[]>([]);
    const [extraTypes, setExtraTypes] = useState<string[]>(['Hôtesse LBE', 'Agent de sécurité', 'Extra Service', 'Extra Cuisine']);
    // Catalogue "Partenaires Extras" (module Extras) — source des types de renfort proposés par le bouton
    // "Ajouter un extra" du Planning (filtrés sur scope_planning dans AddShiftModal). La demande à un
    // partenaire externe se fait désormais uniquement depuis la fiche partenaire (retiré d'ici, 2026-08-20).
    const [extrasPartners, setExtrasPartners] = useState<{ id: string; name: string; types: { id: string; label: string; scope_planning?: boolean | number }[] }[]>([]);
    useEffect(() => {
        moduleApi.getExtrasPartners().then((d: any) => setExtrasPartners(Array.isArray(d) ? d : [])).catch(() => setExtrasPartners([]));
    }, []);


    // Core data from CRM/RH
    const [dbEmployees, setDbEmployees] = useState<any[]>([]);
    const [roles, setRoles] = useState<{ id: string, label: string }[]>([]);

    const [viewMode, setViewMode] = useState<'SEMAINE' | 'JOUR'>('SEMAINE');
    const [selectedDayIndex, setSelectedDayIndex] = useState(0);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // LES LISTES SONT RELUES A CHAQUE OUVERTURE DE "AJOUTER".
    // Elles n'etaient chargees qu'au montage de l'ecran : un type de renfort cree juste avant restait
    // invisible dans le menu jusqu'a un rechargement complet de la page. Signale par Emeni (2026-08-27) —
    // "quand des types extras sont ajoutes, ils ne se mettent pas a jour dans le bouton ajouter".
    // Les deux sources sont relues, car un type peut naitre dans Parametres > Planning comme dans un tout
    // autre module (Partenaires Extras) : l'ecran ne peut pas savoir d'ou vient le changement.
    useEffect(() => {
        if (!isAddModalOpen) return;
        moduleApi.getExtrasPartners()
            .then((d: any) => { if (Array.isArray(d)) setExtrasPartners(d); })
            .catch(() => { /* on garde la liste deja affichee */ });
        planningApi.getSettings()
            .then((st: any) => {
                const liste = Array.isArray(st?.extraTypes) ? st.extraTypes.map((v: any) => String(v)).filter(Boolean) : null;
                if (liste) setExtraTypes(liste);
            })
            .catch(() => { /* idem */ });
    }, [isAddModalOpen]);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isNewPlanningModalOpen, setIsNewPlanningModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // Filtre par poste (demande d'Emeni : "un filtre qui permet d'afficher que certains postes") — liste
    // vide = tous les postes affichés (comportement d'origine, aucun filtre actif).
    const [selectedRoleFilter, setSelectedRoleFilter] = useState<string[]>([]);
    const [roleFilterOpen, setRoleFilterOpen] = useState(false);
    const [view, setView] = useState<'dashboard' | 'grid'>(() => {
        // Une adresse qui porte une semaine ouvre directement la grille sur cette semaine.
        if (parseWeekParam(new URLSearchParams(window.location.search).get('week'))) return 'grid';
        try {
            const rawUser = localStorage.getItem('user');
            const parsed = rawUser ? JSON.parse(rawUser) : null;
            return parsed?.type === 'employe' ? 'grid' : 'dashboard';
        } catch {
            return 'dashboard';
        }
    });
    const [weeksList, setWeeksList] = useState<any[]>([]);
    const [isLoadingWeeks, setIsLoadingWeeks] = useState(false);
    const [archives, setArchives] = useState<any[]>([]);
    const [archiveSearch, setArchiveSearch] = useState('');
    const [archiveYearFilter, setArchiveYearFilter] = useState<string>('');
    const [weeklyDefaults, setWeeklyDefaults] = useState<Record<string, any>>({});
    const [dbAbsences, setDbAbsences] = useState<any[]>([]);
    const [showInfoModal, setShowInfoModal] = useState(true);
    const [premiumRules, setPremiumRules] = useState<{
        soir: { enabled: boolean; afterHour: string; percent: number };
        nuit: { enabled: boolean; afterHour: string; percent: number };
        weekend: { enabled: boolean; percent: number };
    } | null>(null);
    const [showCostPanel, setShowCostPanel] = useState(false);

    const getDefaultShiftForEmployeeDay = useCallback((employeeId: string, date: string, dayIndex: number, currentTemplates: Template[], currentDefaults: any): Shift => {
        const defaultMap = (currentDefaults || {})[String(employeeId)] || {};
        const defaultKey = defaultMap[String(dayIndex)] || defaultMap[['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'][dayIndex]];
        if (!defaultKey || defaultKey === 'REPOS') {
            return {
                date,
                type: 'repos',
                serviceType: 'none',
                segments: [{ type: 'code', label: 'REPOS' }],
            };
        }

        const template = (Array.isArray(currentTemplates) ? currentTemplates : []).find((t: Template) => String(t.id) === String(defaultKey));
        if (!template || !Array.isArray(template.slots) || template.slots.length === 0) {
            return {
                date,
                type: 'repos',
                serviceType: 'none',
                segments: [{ type: 'code', label: 'REPOS' }],
            };
        }

        return {
            date,
            type: 'travail',
            serviceType: template.serviceType || 'midi+soir',
            segments: template.slots.map((slot: any) => ({
                type: 'horaire' as const,
                start: String(slot?.start || '10:00'),
                end: String(slot?.end || '15:00'),
                templateId: template.id,
                color: template.color,
            })),
        };
    }, []);

    const [editingShift, setEditingShift] = useState<{ shift: Shift, employeeId: string, employeeName: string, employeeRole: string, date: string } | null>(null);
    // Annuler plutôt que confirmer (passation §6) : les suppressions réversibles passent par le
    // bandeau d'annulation, les irréversibles par une confirmation qui nomme ce qui disparaît.
    const { push: pushUndo } = useUndo();
    const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
    const [editorPos, setEditorPos] = useState({ x: 0, y: 0 });

    const [openDayMenu, setOpenDayMenu] = useState<string | null>(null);
    const [clipboardDay, setClipboardDay] = useState<{ shifts: Record<string, Shift> } | null>(null);
    const [collapsedRoles, setCollapsedRoles] = useState<Set<string>>(new Set());
    const [rolesOrder, setRolesOrder] = useState<string[]>([]);
    const planningTableRef = useRef<HTMLDivElement>(null);

    const triggerAutoArchive = useCallback(async (weekStart: string, emps: any[], jobPosts: any[], sets: any) => {
        try {
            const p = await planningApi.getWeek(weekStart);
            if (!p) return;

            const roles = jobPosts.map((jp: any) => ({ id: jp.id, label: jp.title }));
            const rolesOrder = sets?.rolesOrder || [];

            const doc = await generatePlanningGridPDF({
                planning: p,
                roles,
                rolesOrder,
                employees: emps,
                companyName: user?.company_name || "Votre Établissement"
            });

            const pdfBase64 = doc.output('datauristring');
            const weekDate = parseISO(weekStart);
            const year = getYear(weekDate);
            const weekNum = getISOWeek(weekDate);
            const filename = `Planning_Hebdo_S${weekNum}_${year}.pdf`;

            await planningApi.archiveWeek({
                week_start: weekStart,
                year,
                week_number: weekNum,
                pdf_base64: pdfBase64,
                filename
            });

            const updatedArchs = await planningApi.getArchives();
            setArchives(updatedArchs || []);
        } catch (e) {
            console.error('Failed auto archive', e);
        }
    }, []);

    const handleOpenArchive = async (weekStart: string) => {
        try {
            const arch = await planningApi.getArchive(weekStart);
            if (arch && arch.pdf_base64) {
                const link = document.createElement('a');
                link.href = arch.pdf_base64;
                link.download = arch.filename;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (e) {
            console.error('Failed to open archive', e);
            alert('Impossible de charger l\'archive PDF.');
        }
    };

    const isWeekLocked = (weekStart: string | Date) => {
        const start = typeof weekStart === 'string' ? parseISO(weekStart) : weekStart;
        const end = addDays(start, 7); // Monday 00:00 next week
        return isBefore(end, new Date());
    };

    const loadData = useCallback(async () => {
        try {
            if (user?.type === 'employe') {
                const weekStr = format(currentWeekStart, 'yyyy-MM-dd');
                const [posts, pData] = await Promise.all([
                    moduleApi.getJobPosts(),
                    planningApi.getWeek(weekStr)
                ]);
                setRoles((posts || []).map((p: any) => ({ id: p.id, label: p.title })));
                setPlanning(pData);
                return;
            }
            const [posts, emps, tpls, planningSettings, archs, absencesData] = await Promise.all([
                moduleApi.getJobPosts(),
                moduleApi.getEmployes(),
                planningApi.getTemplates(),
                planningApi.getSettings(),
                planningApi.getArchives(),
                planningApi.getAbsences()
            ]);
            setArchives(archs || []);
            setDbAbsences(Array.isArray(absencesData) ? absencesData : []);

            const finalRoles = (posts || []).map((p: any) => ({ id: p.id, label: p.title }));

            setRoles(finalRoles);
            setDbEmployees(emps || []);
            setTemplates(Array.isArray(tpls) ? tpls : []);

            const fetchedWeeklyDefaults = (planningSettings && typeof planningSettings === 'object' && (planningSettings as any).weeklyDefaults && typeof (planningSettings as any).weeklyDefaults === 'object')
                ? (planningSettings as any).weeklyDefaults
                : {};
            setWeeklyDefaults(fetchedWeeklyDefaults);
            const weeklyDefaultsRef = fetchedWeeklyDefaults; // local ref for current function scope
            const fetchedPremiumRules = (planningSettings as any)?.premiumRules;
            setPremiumRules(fetchedPremiumRules && typeof fetchedPremiumRules === 'object' ? fetchedPremiumRules : null);
            const configuredAbsenceTypes = Array.isArray((planningSettings as any)?.absenceCodes)
                ? (planningSettings as any).absenceCodes.map((v: any) => {
                    const codeStr = v?.code || v?.label || (typeof v === 'string' ? v : 'ABS');
                    return {
                        code: String(codeStr),
                        isFullDay: v?.isFullDay ?? true,
                        autoApply: v?.autoApply ?? true,
                        color: v?.color || (String(codeStr) === 'REPOS' ? '#000000' : '#ffe39b')
                    };
                })
                : ABSENCE_TYPES.map(c => ({ code: c, isFullDay: true, autoApply: true, color: (String(c) === 'REPOS' ? '#000000' : '#ffe39b') }));

            const configuredExtraTypes = Array.isArray((planningSettings as any)?.extraTypes)
                ? (planningSettings as any).extraTypes.map((v: any) => String(v)).filter(Boolean)
                : ['Hôtesse LBE', 'Agent de sécurité', 'Extra Service', 'Extra Cuisine'];

            setAbsenceTypes(configuredAbsenceTypes);
            setExtraTypes(configuredExtraTypes);

            const configuredRolesOrder = Array.isArray((planningSettings as any)?.rolesOrder)
                ? (planningSettings as any).rolesOrder.map((v: any) => String(v))
                : [];
            setRolesOrder(Array.from(new Set(configuredRolesOrder)));

            const weekStr = format(currentWeekStart, 'yyyy-MM-dd');
            let pData = await planningApi.getWeek(weekStr);

            // Create or update planning for the week
            let p: Planning = pData || {
                id: crypto.randomUUID(),
                weekStart: weekStr,
                weekEnd: format(addDays(currentWeekStart, 6), 'yyyy-MM-dd'),
                service: 'Salle',
                status: 'active',
                rows: [],
                extraShifts: [],
                createdAt: Date.now()
            };

            // Synchronize rows with current active employees
            let newRows = [...p.rows];
            const activeEmps = (emps || []).filter((e: any) => e.first_name); // basic filter
            let hasChanges = false;

            activeEmps.forEach((emp: any) => {
                const lastName = (emp.last_name || '').toUpperCase();
                const firstName = emp.first_name || '';
                const fullName = `${lastName} ${firstName}`.trim();
                const roleId = emp.position || 'GENERAL';

                let existingRow = newRows.find(r => r.employeeId === emp.id);
                if (existingRow) {
                    if (existingRow.employeeName !== fullName || existingRow.employeeRole !== roleId) {
                        existingRow.employeeName = fullName;
                        existingRow.employeeRole = roleId;
                        hasChanges = true;
                    }
                } else {
                    hasChanges = true;
                    const shifts: Record<string, Shift> = {};
                    for (let i = 0; i < 7; i++) {
                        const d = format(addDays(currentWeekStart, i), 'yyyy-MM-dd');
                        const defaultShift = getDefaultShiftForEmployeeDay(String(emp.id), d, i, Array.isArray(tpls) ? tpls : [], weeklyDefaultsRef);
                        
                        // Check for long term absence
                        const activeAbs = (absencesData || []).find((abs: any) => 
                            String(abs.employee_id) === String(emp.id) && 
                            d >= abs.start_date && d <= abs.end_date
                        );

                        if (activeAbs && defaultShift.type !== 'repos') {
                            shifts[d] = {
                                date: d,
                                type: 'absence',
                                serviceType: 'none',
                                segments: [{ type: 'code', label: activeAbs.absence_type || 'CP' }]
                            };
                        } else {
                            shifts[d] = defaultShift;
                        }
                    }
                    newRows.push({
                        employeeId: emp.id, employeeName: fullName, employeeRole: roleId,
                        isExtra: false, shifts
                    });
                }
            });

            // Remove rows of employees who are no longer active (only for non-locked weeks)
            if (!isWeekLocked(currentWeekStart)) {
                const activeEmpIds = new Set((emps || []).map((e: any) => String(e.id)));
                const filteredRows = newRows.filter(r => r.isExtra || activeEmpIds.has(String(r.employeeId)));
                if (filteredRows.length !== newRows.length) {
                    newRows = filteredRows;
                    hasChanges = true;
                }
            }

            // 5. If brand new planning, apply all defaults
            if (!pData) {
                newRows = newRows.map((row) => {
                    const shifts: Record<string, Shift> = {};
                    for (let i = 0; i < 7; i++) {
                        const d = format(addDays(currentWeekStart, i), 'yyyy-MM-dd');
                        const defaultShift = getDefaultShiftForEmployeeDay(String(row.employeeId), d, i, Array.isArray(tpls) ? tpls : [], fetchedWeeklyDefaults);
                        
                        // Check for long term absence
                        const activeAbs = (absencesData || []).find((abs: any) => 
                            String(abs.employee_id) === String(row.employeeId) && 
                            d >= abs.start_date && d <= abs.end_date
                        );

                        if (activeAbs && defaultShift.type !== 'repos') {
                            shifts[d] = {
                                date: d,
                                type: 'absence',
                                serviceType: 'none',
                                segments: [{ type: 'code', label: activeAbs.absence_type || 'CP' }]
                            };
                        } else {
                            shifts[d] = defaultShift;
                        }
                    }
                    return { ...row, shifts };
                });
                hasChanges = true;
            } else {
                // If existing Active/Future planning, ensure new employees or empty rows get defaults
                const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
                if (currentWeekStart >= monday) {
                    newRows = newRows.map(row => {
                        // Check if this row is essentially "empty" (only REPOS or no shifts)
                        const hasWork = Object.values(row.shifts || {}).some(s => s.type === 'travail');
                        if (!hasWork) {
                            const shifts: Record<string, Shift> = { ...row.shifts };
                            for (let i = 0; i < 7; i++) {
                                const d = format(addDays(currentWeekStart, i), 'yyyy-MM-dd');
                                if (!shifts[d] || shifts[d].type === 'repos') {
                                    const defaultShift = getDefaultShiftForEmployeeDay(String(row.employeeId), d, i, Array.isArray(tpls) ? tpls : [], fetchedWeeklyDefaults);
                                    
                                    // Check for long term absence
                                    const activeAbs = (absencesData || []).find((abs: any) => 
                                        String(abs.employee_id) === String(row.employeeId) && 
                                        d >= abs.start_date && d <= abs.end_date
                                    );

                                    if (activeAbs && defaultShift.type !== 'repos') {
                                        shifts[d] = {
                                            date: d,
                                            type: 'absence',
                                            serviceType: 'none',
                                            segments: [{ type: 'code', label: activeAbs.absence_type || 'CP' }]
                                        };
                                    } else {
                                        shifts[d] = defaultShift;
                                    }
                                }
                            }
                            hasChanges = true;
                            return { ...row, shifts };
                        }
                        return row;
                    });
                }
            }

            if (hasChanges || !pData) {
                p.rows = newRows;
                await planningApi.saveWeek({ ...p, weekStart: weekStr });
            }

            setPlanning(p);
        } catch (e) {
            console.error('Failed to load planning data:', e);
        }
    }, [currentWeekStart]);

    const fetchWeeksList = useCallback(async () => {
        setIsLoadingWeeks(true);
        try {
            const [list, archs, sets, emps, jobPosts] = await Promise.all([
                planningApi.listWeeks(),
                planningApi.getArchives(),
                planningApi.getSettings(),
                moduleApi.getEmployes(),
                moduleApi.getJobPosts()
            ]);

            setWeeksList(list || []);
            setArchives(archs || []);

            // Auto-archive check
            const now = new Date();
            const finishedWeeks = (list || []).filter((w: any) => {
                const end = addDays(parseISO(w.week_start), 7);
                return isBefore(end, now);
            });

            for (const fw of finishedWeeks) {
                const alreadyArchived = (archs || []).some((a: any) => a.week_start === fw.week_start);
                if (!alreadyArchived) {
                    await triggerAutoArchive(fw.week_start, emps, jobPosts, sets);
                }
            }
        } catch (e) {
            console.error('Failed to fetch weeks list:', e);
        } finally {
            setIsLoadingWeeks(false);
        }
    }, [triggerAutoArchive]);

    useEffect(() => {
        if (view === 'dashboard') {
            fetchWeeksList();
        } else {
            loadData();
        }
    }, [view, loadData, fetchWeeksList]);

    /**
     * Point d'entrée unique pour changer de semaine : les flèches ‹ ›, l'ouverture d'une semaine
     * depuis le dashboard et la création d'un planning passent tous par ici. On met l'adresse à jour
     * puis on repositionne la semaine courante — le chargement lui-même ne change pas, c'est toujours
     * loadData qui se redéclenche parce qu'il dépend de currentWeekStart.
     */
    const goToWeek = useCallback((date: Date) => {
        const monday = startOfWeek(date, { weekStartsOn: 1 });
        const next = new URLSearchParams(searchParams);
        next.set('week', format(monday, 'yyyy-MM-dd'));
        setSearchParams(next);
        setCurrentWeekStart(monday);
        setView('grid');
    }, [searchParams, setSearchParams]);

    const handleBackToDashboard = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('week');
        setSearchParams(next);
        setView('dashboard');
    }, [searchParams, setSearchParams]);

    // Retour arrière / avant du navigateur : l'adresse fait foi, on recale l'affichage dessus.
    useEffect(() => {
        const fromUrl = parseWeekParam(searchParams.get('week'));
        if (fromUrl) {
            setCurrentWeekStart(prev => (format(prev, 'yyyy-MM-dd') === format(fromUrl, 'yyyy-MM-dd') ? prev : fromUrl));
            setView('grid');
        } else if (user?.type !== 'employe') {
            // Les salariés n'ont pas de dashboard de semaines : leur vue reste la grille.
            setView('dashboard');
        }
    }, [searchParams, user?.type]);

    const handleOpenWeek = (week: any) => {
        const dateStr = typeof week === 'string' ? week : week?.week_start;
        if (!dateStr) return;
        goToWeek(parseISO(dateStr));
    };

    const handleDeleteWeek = async (id: string) => {
        const week = weeksList.find((w: any) => String(w.id) === String(id));
        const label = week?.week_start ? `la semaine du ${format(parseISO(week.week_start), 'd MMMM yyyy', { locale: fr })}` : 'ce planning';
        setConfirmRequest({
            title: 'Supprimer le planning ?',
            description: <>Le planning de {label} sera effacé : tous ses créneaux, ses renforts et ses notes disparaissent.</>,
            confirmLabel: 'Supprimer le planning',
            onConfirm: async () => {
                try {
                    await planningApi.deleteWeek(id);
                    fetchWeeksList();
                } catch (e) {
                    alert('Erreur lors de la suppression');
                }
            },
        });
    };

    const handleDeleteArchive = async (weekStart: string) => {
        setConfirmRequest({
            title: 'Supprimer l\'archive ?',
            description: <>L'archive de la semaine du {format(parseISO(weekStart), 'd MMMM yyyy', { locale: fr })} sera effacée définitivement.</>,
            confirmLabel: 'Supprimer l\'archive',
            onConfirm: () => deleteArchiveNow(weekStart),
        });
    };

    const deleteArchiveNow = async (weekStart: string) => {
        try {
            // Dans planning_archives, l'id est souvent le week_start ou un UUID. 
            // ListWeeks nous renvoie les archives aussi.
            // On va chercher l'archive correspondante dans weeksList
            const arch = archives.find(w => w.week_start === weekStart);
            if (arch) {
                await planningApi.deleteArchive(arch.id);
                fetchWeeksList();
            }
        } catch (e) {
            alert('Erreur lors de la suppression de l\'archive');
        }
    };

    const handleCreateNewPlanning = (monday: string) => {
        goToWeek(parseISO(monday));
        setIsNewPlanningModalOpen(false);
    };

    const savePlanning = async (updated: Planning) => {
        try {
            await planningApi.saveWeek({ ...updated, weekStart: format(currentWeekStart, 'yyyy-MM-dd') });
            setPlanning(updated);
        } catch (err) {
            console.error('Failed to save planning:', err);
        }
    };

    const handleShiftUpdate = (updatedShift: Shift) => {
        if (!planning || !editingShift) return;
        const newRows = planning.rows.map(row => {
            if (row.employeeId === editingShift.employeeId) {
                return { ...row, shifts: { ...row.shifts, [editingShift.date]: updatedShift } };
            }
            return row;
        });
        savePlanning({ ...planning, rows: newRows });
    };

    const handleAddShiftData = async (data: any) => {
        if (!planning) return;

        if (data.mode === 'absenceRange') {
            const startDateStr = String(data.startDate || '');
            const endDateStr = String(data.endDate || '');
            const employeeId = String(data.employeeId || '');
            const absenceCode = String(data.absenceType || 'CP');
            if (!employeeId || !startDateStr || !endDateStr) return;

            const start = parseISO(startDateStr);
            const end = parseISO(endDateStr);

            // 1. Enregistrer l'absence dans la table centrale pour gestion future
            try {
                await planningApi.saveAbsence({
                    employee_id: employeeId,
                    start_date: startDateStr,
                    end_date: endDateStr,
                    absence_type: absenceCode
                });
            } catch (e) {
                console.error('Failed to save central absence', e);
            }

            // 2. Collect all weeks affected
            const weekMondays: string[] = [];
            let curr = startOfWeek(start, { weekStartsOn: 1 });
            while (curr <= end) {
                weekMondays.push(format(curr, 'yyyy-MM-dd'));
                curr = addWeeks(curr, 1);
            }

            for (const monday of weekMondays) {
                try {
                    let p: Planning | null = null;
                    if (monday === format(currentWeekStart, 'yyyy-MM-dd')) {
                        p = planning;
                    } else {
                        p = await planningApi.getWeek(monday);
                    }

                    if (!p) continue; // NE PAS CRÉER de plannings manquants

                    // Apply absence to existing planning
                    let hasChanges = false;
                    const updatedRows = p.rows.map(row => {
                        if (String(row.employeeId) !== employeeId) return row;
                        const newShifts = { ...row.shifts };

                        const weekStart = parseISO(monday);
                        for (let i = 0; i < 7; i++) {
                            const dayDate = addDays(weekStart, i);
                            const dStr = format(dayDate, 'yyyy-MM-dd');

                            if (dStr >= startDateStr && dStr <= endDateStr) {
                                const existingShift = newShifts[dStr];
                                const isRepos = existingShift?.type === 'repos' ||
                                    existingShift?.segments?.some(s => s.label === 'REPOS') ||
                                    !existingShift;

                                if (!isRepos) {
                                    newShifts[dStr] = {
                                        date: dStr,
                                        type: 'absence',
                                        serviceType: 'none',
                                        segments: [{ type: 'code', label: absenceCode }],
                                    };
                                    hasChanges = true;
                                }
                            }
                        }
                        return { ...row, shifts: newShifts };
                    });

                    if (hasChanges) {
                        const finalPlanning = { ...p, rows: updatedRows };
                        await planningApi.saveWeek(finalPlanning);
                        if (monday === format(currentWeekStart, 'yyyy-MM-dd')) {
                            setPlanning(finalPlanning);
                        }
                    }
                } catch (e) {
                    console.error('Failed to apply absence to week', monday, e);
                }
            }
            
            // Recharger les absences locales pour les futurs affichages
            try {
                const updatedAbs = await planningApi.getAbsences();
                setDbAbsences(updatedAbs);
            } catch (e) {}

            fetchWeeksList();
            return;
        }

        if (data.isExtra && data.date) {
            const segment = data.shift.segments[0];
            if (segment && segment.start && segment.end) {
                const label = data.extraType || "Extra";
                const addedCount = data.extraCount || 1;
                // Un meme type d'extra ajoute plusieurs fois sur le meme jour/creneau se cumule sur une
                // seule ligne (ex: 3x Agent de securite) plutot que d'empiler des lignes identiques
                // (signale par Emeni : "3 extras securite" affichait 3 lignes separees au lieu d'une seule).
                const existingExtras = planning.extraShifts || [];
                const existingIndex = existingExtras.findIndex((e) => e.label === label && e.date === data.date && e.start === segment.start && e.end === segment.end);
                let newExtraShifts;
                if (existingIndex !== -1) {
                    newExtraShifts = existingExtras.map((e, i) => i === existingIndex ? { ...e, count: e.count + addedCount } : e);
                } else {
                    const newExtra: ExtraShift = {
                        id: crypto.randomUUID(),
                        label,
                        date: data.date,
                        start: segment.start,
                        end: segment.end,
                        count: addedCount
                    };
                    newExtraShifts = [...existingExtras, newExtra];
                }
                savePlanning({ ...planning, extraShifts: newExtraShifts });
            }
        } else if (data.date) {
            let updatedRows = [...planning.rows];
            const existingRowIndex = updatedRows.findIndex(r => r.employeeId === data.employeeId);
            if (existingRowIndex !== -1) {
                updatedRows[existingRowIndex] = {
                    ...updatedRows[existingRowIndex],
                    shifts: { ...updatedRows[existingRowIndex].shifts, [data.date]: data.shift }
                };
                savePlanning({ ...planning, rows: updatedRows });
            }
        }
    };

    const handleDeleteExtra = (extraId: string) => {
        if (!planning) return;
        const previous = planning;
        const extra = (planning.extraShifts || []).find(e => e.id === extraId);
        savePlanning({ ...planning, extraShifts: (planning.extraShifts || []).filter(e => e.id !== extraId) });
        pushUndo({
            message: extra ? `Renfort ${extra.count}× ${extra.label} supprimé — ${format(parseISO(extra.date), 'EEEE d', { locale: fr })} ${extra.start}-${extra.end}` : 'Renfort supprimé',
            undo: () => savePlanning(previous),
            undoneMessage: 'Renfort rétabli',
        });
    };

    const handleCopyDay = (date: string) => {
        if (!planning) return;
        const shifts: Record<string, Shift> = {};
        planning.rows.forEach(row => { if (row.shifts[date]) shifts[row.employeeId] = row.shifts[date]; });
        setClipboardDay({ shifts });
        setOpenDayMenu(null);
    };

    const handlePasteDay = (targetDate: string) => {
        if (!planning || !clipboardDay) return;
        const newRows = planning.rows.map(row => {
            const shiftToPaste = clipboardDay.shifts[row.employeeId];
            if (shiftToPaste) return { ...row, shifts: { ...row.shifts, [targetDate]: { ...shiftToPaste, date: targetDate } } };
            return row;
        });
        savePlanning({ ...planning, rows: newRows });
        setOpenDayMenu(null);
    };

    const handleClearDay = (date: string) => {
        if (!planning) return;
        const previous = planning;
        const cleared = planning.rows.filter(row => row.shifts[date]?.segments?.some(sg => sg.type === 'horaire')).length;
        pushUndo({
            message: `Journée du ${format(parseISO(date), 'EEEE d MMMM', { locale: fr })} vidée — ${cleared} créneau${cleared > 1 ? 'x' : ''} mis au repos`,
            undo: () => savePlanning(previous),
            undoneMessage: 'Journée rétablie',
        });
        const newRows = planning.rows.map(row => ({
            ...row,
            shifts: { 
                ...row.shifts, 
                [date]: { 
                    date, 
                    type: 'repos' as const, 
                    serviceType: 'none' as const, 
                    segments: [{ type: 'code' as const, label: 'REPOS' }],
                    isManual: true
                } 
            }
        }));
        savePlanning({ ...planning, rows: newRows });
        setOpenDayMenu(null);
    };

    const getShiftCounts = (shift: Shift | undefined) => getShiftCountsShared(shift, absenceTypes);

    const getMinutes = (time: string | undefined) => {
        if (!time || typeof time !== 'string') return 0;
        const [h, m] = time.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    const calculateShiftDuration = (shift: Shift | undefined) => {
        if (!shift || !shift.segments) return 0;
        return shift.segments.reduce((acc, seg) => {
            if (seg.type === 'horaire' && seg.start && seg.end) {
                let start = getMinutes(seg.start);
                let end = getMinutes(seg.end);
                if (end < start) end += 24 * 60;
                return acc + (end - start);
            }
            return acc;
        }, 0);
    };

    const formatDuration = (minutes: number) => {
        if (minutes === 0) return null;
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
    };

    const getKPIs = (dayDate: string) => {
        if (!planning) return { midi: 0, soir: 0, extras: [] as ExtraShift[] };
        let midi = 0; let soir = 0;
        planning.rows.forEach(row => {
            const counts = getShiftCounts(row?.shifts?.[dayDate]);
            midi += counts.midi;
            soir += counts.soir;
        });
        const dayExtras = (planning.extraShifts || []).filter(e => e.date === dayDate);
        dayExtras.forEach(e => {
            let m = 0; let s = 0;
            const isEvening = (e.end && (getMinutes(e.end) < 360 || getMinutes(e.end) > 1140));
            if (e.start < "12:00") { m = e.count; if (isEvening) s = e.count; }
            else if (e.start >= "16:00") { s = e.count; }
            else { if (isEvening) s = e.count; else m = e.count; }
            midi += m; soir += s;
        });
        return { midi, soir, extras: dayExtras };
    };

    const getGroupTotals = (rows: PlanningRow[], date: string) => {
        let midi = 0;
        let soir = 0;
        rows.forEach((r) => {
            const counts = getShiftCounts(r.shifts[date]);
            midi += counts.midi;
            soir += counts.soir;
        });
        return { midi, soir };
    };

    // Coût de la semaine : calculé à la volée côté client à partir des segments "horaire" déjà chargés,
    // du taux horaire de l'employé (dbEmployees[].salary, jamais renvoyé au rôle employé côté API) et des
    // règles de primes de Paramètres > Primes Horaires. Un segment qui démarre après le seuil "soir"/"nuit"
    // compte pour toute sa durée dans la tranche concernée (pas de découpage à la minute) ; weekend et
    // soir/nuit peuvent se cumuler sur un même créneau.
    const parseHM = (t?: string): number | null => {
        if (!t) return null;
        const [h, m] = t.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
    };

    const computeSegmentCost = useCallback((segment: ShiftSegment, dateStr: string, hourlyRate: number) => {
        if (segment.type !== 'horaire' || !segment.start || !segment.end) return { hours: 0, cost: 0 };
        const startMin = parseHM(segment.start);
        let endMin = parseHM(segment.end);
        if (startMin === null || endMin === null) return { hours: 0, cost: 0 };
        if (endMin <= startMin) endMin += 24 * 60; // créneau à cheval sur minuit
        const hours = (endMin - startMin) / 60;
        if (!hourlyRate || hourlyRate <= 0) return { hours, cost: 0 };

        let multiplier = 1;
        if (premiumRules) {
            const dayOfWeek = new Date(dateStr + 'T00:00:00').getDay(); // 0 = dimanche, 6 = samedi
            if (premiumRules.weekend?.enabled && (dayOfWeek === 0 || dayOfWeek === 6)) {
                multiplier += (premiumRules.weekend.percent || 0) / 100;
            }
            if (premiumRules.soir?.enabled) {
                const threshold = parseHM(premiumRules.soir.afterHour);
                if (threshold !== null && startMin >= threshold) multiplier += (premiumRules.soir.percent || 0) / 100;
            }
            if (premiumRules.nuit?.enabled) {
                const threshold = parseHM(premiumRules.nuit.afterHour);
                if (threshold !== null && startMin >= threshold) multiplier += (premiumRules.nuit.percent || 0) / 100;
            }
        }
        return { hours, cost: hours * hourlyRate * multiplier };
    }, [premiumRules]);

    const weeklyCostByEmployee = useMemo(() => {
        if (!planning) return [];
        return planning.rows.map((row) => {
            const emp = dbEmployees.find((e) => String(e.id) === String(row.employeeId));
            const hourlyRate = Number(emp?.salary) || 0;
            let hours = 0, cost = 0;
            Object.values(row.shifts || {}).forEach((shift) => {
                (shift?.segments || []).forEach((seg) => {
                    const r = computeSegmentCost(seg, shift.date, hourlyRate);
                    hours += r.hours; cost += r.cost;
                });
            });
            return { employeeId: row.employeeId, employeeName: row.employeeName, employeeRole: row.employeeRole, hourlyRate, hours, cost };
        });
    }, [planning, dbEmployees, computeSegmentCost]);

    const weeklyCostByRole = useMemo(() => {
        const map = new Map<string, { roleId: string; hours: number; cost: number; missingRate: boolean }>();
        weeklyCostByEmployee.forEach((e) => {
            const key = e.employeeRole || 'GÉNÉRAL';
            const existing = map.get(key) || { roleId: key, hours: 0, cost: 0, missingRate: false };
            existing.hours += e.hours;
            existing.cost += e.cost;
            if (e.hours > 0 && !e.hourlyRate) existing.missingRate = true;
            map.set(key, existing);
        });
        return Array.from(map.values());
    }, [weeklyCostByEmployee]);

    const weeklyCostTotal = useMemo(() => weeklyCostByEmployee.reduce((sum, e) => sum + e.cost, 0), [weeklyCostByEmployee]);
    const weeklyHasAnyRate = weeklyCostByEmployee.some((e) => e.hourlyRate > 0);
    const formatEuro = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

    const toggleRoleCollapse = (roleId: string) => {
        setCollapsedRoles((prev) => {
            const next = new Set(prev);
            if (next.has(roleId)) next.delete(roleId);
            else next.add(roleId);
            return next;
        });
    };



    const handleResetWeek = () => {
        if (!planning) return;
        const creneaux = planning.rows.reduce((n, r) => n + Object.values(r.shifts || {}).filter(sh => sh?.segments?.some(sg => sg.type === 'horaire')).length, 0);
        const renforts = (planning.extraShifts || []).length;
        setConfirmRequest({
            title: 'Réinitialiser la semaine ?',
            description: <>{creneaux} créneau{creneaux > 1 ? 'x' : ''}, {planning.rows.length} salarié{planning.rows.length > 1 ? 's' : ''}{renforts > 0 ? <> et {renforts} renfort{renforts > 1 ? 's' : ''}</> : null} seront remplacés par les défauts hebdomadaires. Les horaires saisis à la main cette semaine seront perdus.</>,
            confirmLabel: 'Réinitialiser la semaine',
            destructive: false,
            onConfirm: resetWeekNow,
        });
    };

    const resetWeekNow = async () => {
        if (!planning) return;
        try {
            const [settings, tpls] = await Promise.all([planningApi.getSettings(), planningApi.getTemplates()]);
            const weeklyDefaults = (settings && typeof settings === 'object' && (settings as any).weeklyDefaults && typeof (settings as any).weeklyDefaults === 'object')
                ? (settings as any).weeklyDefaults
                : {};
            const templatesList = Array.isArray(tpls) ? tpls : [];
            const weekStartDates = Array.from({ length: 7 }, (_, i) => format(addDays(currentWeekStart, i), 'yyyy-MM-dd'));

            const newRows = planning.rows.map((row) => {
                const newShifts: Record<string, Shift> = { ...row.shifts };
                for (let i = 0; i < 7; i++) {
                    const date = weekStartDates[i];
                    const defaultMap = weeklyDefaults[String(row.employeeId)] || {};
                    const defaultKey = defaultMap[String(i)] || defaultMap[['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'][i]];
                    if (!defaultKey || defaultKey === 'REPOS') {
                        newShifts[date] = { date, type: 'repos', serviceType: 'none', segments: [{ type: 'code', label: 'REPOS' }] };
                        continue;
                    }
                    const tpl = templatesList.find((t: Template) => String(t.id) === String(defaultKey));
                    if (!tpl || !Array.isArray(tpl.slots) || tpl.slots.length === 0) {
                        newShifts[date] = { date, type: 'repos', serviceType: 'none', segments: [{ type: 'code', label: 'REPOS' }] };
                        continue;
                    }
                    newShifts[date] = {
                        date,
                        type: 'travail',
                        serviceType: tpl.serviceType || 'midi+soir',
                        segments: tpl.slots.map((slot: any) => ({
                            type: 'horaire',
                            start: String(slot?.start || '10:00'),
                            end: String(slot?.end || '15:00'),
                            templateId: tpl.id,
                            color: tpl.color,
                        })),
                    };
                }
                return { ...row, shifts: newShifts };
            });

            savePlanning({ ...planning, rows: newRows });
        } catch (e) {
            console.error('Failed to reset week', e);
        }
    };

    const applyWeeklyDefaultsToCurrentWeek = useCallback(async (targetEmployeeIds?: string[]) => {
        if (!planning) return;
        try {
            const [settings, tpls] = await Promise.all([planningApi.getSettings(), planningApi.getTemplates()]);
            const weeklyDefaults = (settings && typeof settings === 'object' && (settings as any).weeklyDefaults && typeof (settings as any).weeklyDefaults === 'object')
                ? (settings as any).weeklyDefaults
                : {};
            const templatesList = Array.isArray(tpls) ? tpls : [];
            const weekDates = Array.from({ length: 7 }, (_, i) => format(addDays(currentWeekStart, i), 'yyyy-MM-dd'));

            const newRows = planning.rows.map((row) => {
                if (targetEmployeeIds && !targetEmployeeIds.includes(String(row.employeeId))) return row;

                const newShifts: Record<string, Shift> = { ...row.shifts };
                for (let i = 0; i < 7; i++) {
                    const date = weekDates[i];

                    // PRESERVER LES ABSENCES : Si le jour a déjà une absence ou un code manuel, on ne touche à rien
                    const existingShift = row.shifts[date];
                    const isAbsence = existingShift?.type === 'absence' || existingShift?.segments?.some(seg => seg.type === 'code' && seg.label !== 'REPOS');
                    if (isAbsence) continue;

                    const defaultMap = weeklyDefaults[String(row.employeeId)] || {};
                    const defaultKey = defaultMap[String(i)] || defaultMap[['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'][i]];
                    if (!defaultKey || defaultKey === 'REPOS') {
                        newShifts[date] = { date, type: 'repos', serviceType: 'none', segments: [{ type: 'code', label: 'REPOS' }] };
                        continue;
                    }
                    const tpl = templatesList.find((t: Template) => String(t.id) === String(defaultKey));
                    if (!tpl || !Array.isArray(tpl.slots) || tpl.slots.length === 0) {
                        newShifts[date] = { date, type: 'repos', serviceType: 'none', segments: [{ type: 'code', label: 'REPOS' }] };
                        continue;
                    }
                    newShifts[date] = {
                        date,
                        type: 'travail',
                        serviceType: tpl.serviceType || 'midi+soir',
                        segments: tpl.slots.map((slot: any) => ({
                            type: 'horaire',
                            start: String(slot?.start || '10:00'),
                            end: String(slot?.end || '15:00'),
                            templateId: tpl.id,
                            color: tpl.color,
                        })),
                    };
                }
                return { ...row, shifts: newShifts };
            });

            await savePlanning({ ...planning, rows: newRows });
        } catch (e) {
            console.error('Failed to apply weekly defaults to current week', e);
        }
    }, [planning, currentWeekStart]);

    const syncDefaultsToAllFutureWeeks = useCallback(async (targetEmployeeIds?: string[]) => {
        try {
            const [weeks, settings, tpls] = await Promise.all([
                planningApi.listWeeks(),
                planningApi.getSettings(),
                planningApi.getTemplates()
            ]);
            
            const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
            const futureWeeks = weeks.filter((w: any) => parseISO(w.week_start) >= monday);
            const weeklyDefaults = (settings as any)?.weeklyDefaults || {};
            const templatesList = Array.isArray(tpls) ? tpls : [];

            for (const w of futureWeeks) {
                const p = await planningApi.getWeek(w.week_start);
                if (!p) continue;

                const weekStart = parseISO(w.week_start);
                const weekDates = Array.from({ length: 7 }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd'));

                let hasChanges = false;
                const updatedRows = p.rows.map((row: any) => {
                    const rowEmpId = String(row.employeeId);
                    if (targetEmployeeIds && !targetEmployeeIds.includes(rowEmpId)) return row;

                    const hasManualWork = Object.values(row.shifts || {}).some((s: any) => (s as any).type === 'travail');
                    if (hasManualWork && !targetEmployeeIds) return row; // Don't overwrite manual work unless explicitly targeted

                    hasChanges = true;
                    const newShifts: Record<string, Shift> = { ...row.shifts };
                    for (let i = 0; i < 7; i++) {
                        const date = weekDates[i];

                        // PRESERVER LES ABSENCES : Si le jour a déjà une absence ou un code manuel, on ne touche à rien
                        const existingShift = row.shifts[date];
                        const isAbsence = existingShift?.type === 'absence' || existingShift?.segments?.some((seg: any) => seg.type === 'code' && seg.label !== 'REPOS');
                        if (isAbsence) continue;

                        const defaultMap = weeklyDefaults[rowEmpId] || {};
                        const defaultKey = defaultMap[String(i)];
                        if (!defaultKey || defaultKey === 'REPOS') {
                            newShifts[date] = { date, type: 'repos', serviceType: 'none', segments: [{ type: 'code', label: 'REPOS' }] };
                        } else {
                            const tpl = templatesList.find((t: any) => String(t.id) === String(defaultKey));
                            if (tpl) {
                                newShifts[date] = {
                                    date, type: 'travail', serviceType: tpl.serviceType || 'midi+soir',
                                    segments: tpl.slots.map((slot: any) => ({
                                        type: 'horaire', start: slot.start, end: slot.end, templateId: tpl.id, color: tpl.color
                                    }))
                                };
                            }
                        }
                    }
                    return { ...row, shifts: newShifts };
                });

                if (hasChanges) {
                    await planningApi.saveWeek({ ...p, rows: updatedRows, weekStart: w.week_start });
                }
            }
        } catch (e) {
            console.error('Failed to sync defaults to future weeks', e);
        }
    }, []);

    const handleSettingsDataChanged = useCallback(async (options?: { applyWeeklyDefaults?: boolean, changedEmployees?: string[] }) => {
        if (options?.applyWeeklyDefaults) {
            await applyWeeklyDefaultsToCurrentWeek(options.changedEmployees);
            await syncDefaultsToAllFutureWeeks(options.changedEmployees);
        }
        await loadData();
    }, [applyWeeklyDefaultsToCurrentWeek, syncDefaultsToAllFutureWeeks, loadData]);

    const groupedRows = useMemo(() => {
        if (!planning) return {};

        // 1. Map for normalization (id -> role, label.toLowerCase() -> role)
        const roleMap = new Map<string, { id: string; label: string }>();
        roles.forEach(r => {
            const rid = String(r.id);
            roleMap.set(rid, r);
            roleMap.set(r.label.toLowerCase(), r);
        });

        // 2. Filter employees by search term (and user type if employee)
        const filtered = planning.rows.filter(r => {
            if (user?.type === 'employe') {
                return String(r.employeeId) === String(user.id);
            }
            const search = searchTerm.toLowerCase();
            if (!search) return true;
            if (r.employeeName.toLowerCase().includes(search)) return true;
            const role = roleMap.get(String(r.employeeRole)) || roleMap.get(String(r.employeeRole).toLowerCase());
            if (role && role.label.toLowerCase().includes(search)) return true;
            if (String(r.employeeRole).toLowerCase().includes(search)) return true;
            return false;
        });

        // 3. Group employees by Role ID (if possible) or standardized key
        const groups = new Map<string, PlanningRow[]>();
        filtered.forEach(r => {
            const role = roleMap.get(String(r.employeeRole)) || roleMap.get(String(r.employeeRole).toLowerCase());
            const key = role ? String(role.id) : String(r.employeeRole);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(r);
        });

        // 4. Sort employees within each group by name
        groups.forEach(rows => {
            rows.sort((a, b) => a.employeeName.localeCompare(b.employeeName, 'fr', { sensitivity: 'base' }));
        });

        const sortedGroups: { roleId: string; rows: PlanningRow[] }[] = [];
        const handledKeys = new Set<string>();

        // If logged in as employee, only return non-empty groups (their specific role)
        if (user?.type === 'employe') {
            groups.forEach((rows, key) => {
                if (rows.length > 0) {
                    sortedGroups.push({ roleId: key, rows });
                }
            });
            return sortedGroups;
        }

        // 5. Apply explicit rolesOrder
        if (rolesOrder && rolesOrder.length > 0) {
            rolesOrder.forEach(id => {
                const sid = String(id);
                if (!handledKeys.has(sid)) {
                    sortedGroups.push({ roleId: sid, rows: groups.get(sid) || [] });
                    handledKeys.add(sid);
                }
            });
        }

        // 6. Add remaining roles from the 'roles' list in order
        roles.forEach(r => {
            const rid = String(r.id);
            if (!handledKeys.has(rid)) {
                sortedGroups.push({ roleId: rid, rows: groups.get(rid) || [] });
                handledKeys.add(rid);
            }
        });

        // 7. Add any remaining groups from the data that were not in the roles list
        groups.forEach((rows, key) => {
            const skey = String(key);
            if (!handledKeys.has(skey)) {
                sortedGroups.push({ roleId: skey, rows });
            }
        });

        // 8. Filtre par poste (demande d'Emeni) — liste vide = aucun filtre, tous les postes affichés.
        if (selectedRoleFilter.length > 0) {
            return sortedGroups.filter(g => selectedRoleFilter.includes(g.roleId));
        }

        return sortedGroups;
    }, [planning, searchTerm, roles, rolesOrder, user, selectedRoleFilter]);

    const getRoleLabel = (roleId: string) => roles.find(r => String(r.id) === String(roleId))?.label || roleId;

    if (view === 'dashboard') {
        return (
            <div className="max-w-[1700px] mx-auto p-4">
                <DashboardView
                    user={user}
                    weeks={weeksList}
                    onOpen={handleOpenWeek}
                    onDelete={handleDeleteWeek}
                    onDeleteArchive={handleDeleteArchive}
                    onNew={() => setIsNewPlanningModalOpen(true)}
                    setIsSettingsOpen={setIsSettingsOpen}
                    isLoading={isLoadingWeeks}
                    handleOpenArchive={handleOpenArchive}
                />
                <NewPlanningModal
                    isOpen={isNewPlanningModalOpen}
                    onClose={() => setIsNewPlanningModalOpen(false)}
                    onCreate={handleCreateNewPlanning}
                />
                {/* Cette vue rend sa propre modale : le return anticipé ci-dessus la privait de celle de la vue
                    semaine, et « Supprimer le planning » / « Supprimer l'archive » ne faisaient plus rien
                    (signalé par Emeni en production le 2026-09-19). */}
                <ConfirmModal request={confirmRequest} onClose={() => setConfirmRequest(null)} />
            </div>
        );
    }

    if (!planning) return <div className="p-10 text-center text-slate-500 dark:text-slate-400">Chargement du planning...</div>;
    const weekStartDates = Array.from({ length: 7 }, (_, i) => format(addDays(currentWeekStart, i), 'yyyy-MM-dd'));
    const displayedDates = viewMode === 'JOUR' ? [weekStartDates[selectedDayIndex]] : weekStartDates;

    return (
        <div className="p-4 md:p-8 min-h-screen ">
            <div className="space-y-5 font-sans text-slate-800 dark:text-slate-200 max-w-[1700px] mx-auto bg-slate-100 dark:bg-[var(--bg-soft)]/50 p-4 rounded-2xl border border-slate-200 dark:border-border">
                <header className="bg-surface border border-border rounded-2xl overflow-hidden">
                    {/* Bande d'ambiance : le seul endroit qui bouge ; rien n'est animé sous elle (maquette 7a). */}
                    <AmbianceBand className="h-[96px]">
                        <div className="h-[96px] px-[18px] py-[15px] flex flex-col justify-between">
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-content-muted truncate">{user?.company_name || ''}</span>
                            </div>
                            <div className="text-[21px] font-bold tracking-[-0.02em] text-content leading-none">Planning</div>
                        </div>
                    </AmbianceBand>

                    <div className="px-[18px] py-3.5 border-t border-border flex flex-wrap items-start justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-3.5">
                            {user?.type !== 'employe' && (
                                <button type="button" onClick={handleBackToDashboard} className="h-[30px] px-2 -ml-2 inline-flex items-center gap-1.5 rounded-lg text-[12.5px] font-semibold text-content-muted hover:text-content whitespace-nowrap shrink-0">
                                    <ArrowLeft size={14} /> Dashboard
                                </button>
                            )}
                            {/* Flèches de semaine : changer de semaine est le geste le plus fréquent du planning,
                                il ne doit pas obliger à repasser par le dashboard. −7 / +7 jours, rien d'autre. */}
                            <div className="flex items-center gap-1 shrink-0">
                                <button type="button" onClick={() => goToWeek(addDays(currentWeekStart, -7))} aria-label="Semaine précédente" title="Semaine précédente" className="w-[30px] h-[30px] flex items-center justify-center rounded-[8px] border border-border bg-surface text-content-muted hover:border-content hover:text-content transition-colors shrink-0">
                                    <ChevronLeft size={14} />
                                </button>
                                <button type="button" onClick={() => goToWeek(addDays(currentWeekStart, 7))} aria-label="Semaine suivante" title="Semaine suivante" className="w-[30px] h-[30px] flex items-center justify-center rounded-[8px] border border-border bg-surface text-content-muted hover:border-content hover:text-content transition-colors shrink-0">
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-baseline gap-2 whitespace-nowrap">
                                    <span className="text-[10.5px] font-semibold uppercase tracking-[.09em] text-content-muted">Semaine du</span>
                                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--acc)' }}>{format(currentWeekStart, 'dd MMMM yyyy', { locale: fr })}</span>
                                </div>
                                <p className="text-[11.5px] text-content-muted tabular-nums whitespace-nowrap">
                                    {planning.rows.length} salarié{planning.rows.length > 1 ? 's' : ''} · {planning.rows.reduce((n, r) => n + Object.values(r.shifts || {}).filter(sh => sh && sh.segments && sh.segments.some(sg => sg.type === 'horaire')).length, 0)} créneaux
                                    {weeklyHasAnyRate && <> · {formatEuro(weeklyCostTotal)}</>}
                                </p>
                            </div>
                            {/* Affichée aussi sur téléphone : la vue « Jour » est la plus lisible sur un petit écran. */}
                            <div className="inline-flex p-[3px] rounded-[9px] bg-surface-soft border border-border shrink-0">
                                <button type="button" onClick={() => setViewMode('SEMAINE')} className={`px-3 py-1.5 rounded-[7px] text-[12.5px] font-semibold whitespace-nowrap transition-all ${viewMode === 'SEMAINE' ? 'bg-surface shadow-sm' : 'text-content-muted'}`} style={viewMode === 'SEMAINE' ? { color: 'var(--acc)' } : undefined}>Semaine</button>
                                <button type="button" onClick={() => setViewMode('JOUR')} className={`px-3 py-1.5 rounded-[7px] text-[12.5px] font-semibold whitespace-nowrap transition-all ${viewMode === 'JOUR' ? 'bg-surface shadow-sm' : 'text-content-muted'}`} style={viewMode === 'JOUR' ? { color: 'var(--acc)' } : undefined}>Jour</button>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-[7px]">
                            {user?.type !== 'employe' && (
                                <div className="relative flex items-center gap-2 h-[34px] px-3 rounded-full border border-border bg-surface-soft/60 shrink-0 focus-within:border-content">
                                    <Search className="text-content-muted shrink-0" size={14} />
                                    <input className="w-28 lg:w-40 bg-transparent text-[12.5px] text-content placeholder:text-content-muted outline-none" placeholder="Filtrer employé" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                </div>
                            )}
                            {user?.type !== 'employe' && roles.length > 0 && (
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setRoleFilterOpen(v => !v)}
                                        className={`h-[34px] px-[13px] inline-flex items-center gap-[7px] rounded-full border text-[12.5px] font-semibold whitespace-nowrap shrink-0 transition-colors ${selectedRoleFilter.length > 0 ? '' : 'border-border bg-surface text-content hover:border-content'}`}
                                        style={selectedRoleFilter.length > 0 ? { background: 'var(--acc-bg)', borderColor: 'var(--acc-bd)', color: 'var(--acc-fg)' } : undefined}
                                        title="Filtrer par poste"
                                    >
                                        <Filter size={14} /> Postes {selectedRoleFilter.length > 0 && `(${selectedRoleFilter.length})`}
                                    </button>
                                        {roleFilterOpen && (
                                            <>
                                                <div className="fixed inset-0 z-30" onClick={() => setRoleFilterOpen(false)} />
                                                <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border rounded-xl shadow-xl z-40 max-h-80 overflow-y-auto">
                                                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-border">
                                                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Postes affichés</span>
                                                        {selectedRoleFilter.length > 0 && (
                                                            <button onClick={() => setSelectedRoleFilter([])} className="text-[11px] font-bold text-brand-500 hover:underline">Tout afficher</button>
                                                        )}
                                                    </div>
                                                    {roles.map(r => {
                                                        const rid = String(r.id);
                                                        const checked = selectedRoleFilter.includes(rid);
                                                        return (
                                                            <button
                                                                key={rid}
                                                                onClick={() => setSelectedRoleFilter(prev => checked ? prev.filter(id => id !== rid) : [...prev, rid])}
                                                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-surface-soft"
                                                            >
                                                                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-brand-600 border-brand-600' : 'border-slate-300 dark:border-border'}`}>
                                                                    {checked && <Check size={11} className="text-brand-fg" />}
                                                                </span>
                                                                <span className="text-slate-700 dark:text-content truncate">{r.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                </div>
                            )}
                            {user?.type !== 'employe' && (
                                <>
                                    <button type="button" onClick={() => setIsAddModalOpen(true)} className="h-[34px] px-[14px] inline-flex items-center gap-[7px] rounded-full bg-brand-500 text-brand-fg text-[12.5px] font-semibold whitespace-nowrap shrink-0 hover:-translate-y-px transition-transform"><Plus size={15} /> Ajouter</button>
                                    <button type="button" onClick={() => navigate(`/feuille-de-route?date=${format(currentWeekStart, 'yyyy-MM-dd')}`)} className="h-[34px] px-[13px] inline-flex items-center gap-[7px] rounded-full border border-border bg-surface text-content text-[12.5px] font-semibold whitespace-nowrap shrink-0 hover:border-content" title="Créer ma feuille de route de la semaine"><MapIcon size={14} /> <span className="hidden lg:inline">Feuille de route</span></button>
                                    <button type="button" onClick={handleResetWeek} className="w-[34px] h-[34px] flex items-center justify-center rounded-full border border-border bg-surface text-content-muted hover:border-content hover:text-content shrink-0" title="Réinitialiser semaine"><RefreshCw size={15} /></button>
                                    <button type="button" onClick={() => setIsExportModalOpen(true)} className="w-[34px] h-[34px] flex items-center justify-center rounded-full border border-border bg-surface text-content-muted hover:border-content hover:text-content shrink-0" title="Exporter PDF"><FileDown size={15} /></button>
                                    {user?.type === 'client' && (
                                        <button type="button" onClick={() => setIsImportModalOpen(true)} className="w-[34px] h-[34px] flex items-center justify-center rounded-full border border-border bg-surface text-content-muted hover:border-content hover:text-content shrink-0" title="Importer un planning Excel"><FileUp size={15} /></button>
                                    )}
                                    {user?.type === 'client' && (
                                        <button type="button" onClick={() => setIsSettingsOpen(true)} className="w-[34px] h-[34px] flex items-center justify-center rounded-full border border-border bg-surface text-content-muted hover:border-content hover:text-content shrink-0" title="Paramètres du planning"><Settings size={15} /></button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </header>

                <div className="py-2 overflow-x-auto scrollbar-hide">
                    <div className="flex gap-4 pb-2 px-1">
                        {weekStartDates.map((date, idx) => {
                            const kpi = getKPIs(date);
                            const isSelected = viewMode === 'JOUR' && idx === selectedDayIndex;
                            return (
                                <div
                                    key={date}
                                    onClick={() => {
                                        setViewMode('JOUR');
                                        setSelectedDayIndex(idx);
                                    }}
                                    className={`flex flex-col p-3 rounded-xl border transition-all min-w-[140px] cursor-pointer ${isSelected ? 'bg-white dark:bg-[var(--bg-card)] border-brand-500 shadow-md ring-1 ring-brand-500/20' : 'bg-white dark:bg-[var(--bg-card)] border-slate-100 dark:border-border hover:border-slate-300 dark:hover:border-white/20 shadow-sm'}`}
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-tighter">
                                            {format(parseISO(date), 'EEE dd', { locale: fr }).toUpperCase()}
                                        </span>
                                    </div>

                                    <div className="flex gap-3 items-center mb-2">
                                        <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                                            Midi : <strong className="text-[#ea580c] dark:text-orange-400 ml-1">{kpi.midi}</strong>
                                        </div>
                                        <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                                            Soir : <strong className="text-[#2563eb] dark:text-blue-400 ml-1">{kpi.soir}</strong>
                                        </div>
                                    </div>

                                    {kpi.extras.length > 0 && (
                                        <div className="space-y-1 mt-1">
                                            {kpi.extras.map((extra) => (
                                                <div key={extra.id} className="group relative bg-[#f5f3ff] dark:bg-purple-500/10 rounded-lg p-2 border border-purple-100 dark:border-purple-500/20 transition-all hover:shadow-sm">
                                                    <div className="flex justify-between items-center gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-[11px] font-black text-purple-900 dark:text-purple-300 uppercase leading-tight truncate">
                                                                {extra.count}x {extra.label}
                                                            </div>
                                                            <div className="text-[9px] text-purple-500 dark:text-purple-400 font-bold mt-0.5">
                                                                {extra.start}-{extra.end}
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteExtra(extra.id); }}
                                                            className="text-purple-300 hover:text-red-500 transition-colors p-0.5"
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {user?.type !== 'employe' && (
                    <div className="bg-white dark:bg-[var(--bg-card)] rounded-2xl shadow-sm border border-slate-200 dark:border-border overflow-hidden">
                        <button onClick={() => setShowCostPanel((v) => !v)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-surface-soft transition-colors">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-brand-500/10 flex items-center justify-center shrink-0">
                                    <Wallet size={15} className="text-brand-500" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-slate-800 dark:text-content">Coût de la semaine</p>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{weeklyHasAnyRate ? `${weeklyCostByEmployee.reduce((s, e) => s + e.hours, 0).toFixed(1)}h travaillées` : 'Aucun taux horaire renseigné pour l\'instant'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {weeklyHasAnyRate && <span className="text-base font-black text-brand-600 dark:text-brand-400">{formatEuro(weeklyCostTotal)}</span>}
                                {showCostPanel ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                            </div>
                        </button>
                        {showCostPanel && (
                            <div className="px-4 pb-4 space-y-2 border-t border-slate-100 dark:border-border pt-3">
                                {!weeklyHasAnyRate ? (
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Renseignez un taux horaire dans la fiche d'un employé (Postes &amp; Employés) pour voir apparaître le coût de la semaine ici.</p>
                                ) : (
                                    <>
                                        {weeklyCostByRole.map((r) => (
                                            <div key={r.roleId} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 dark:border-white/5 last:border-0">
                                                <span className="font-bold text-slate-600 dark:text-slate-300">{getRoleLabel(r.roleId)}</span>
                                                <div className="flex items-center gap-3">
                                                    {r.missingRate && <span className="text-[10px] text-amber-600 dark:text-amber-400">taux manquant sur ≥1 employé</span>}
                                                    <span className="text-slate-500 dark:text-slate-400">{r.hours.toFixed(1)}h</span>
                                                    <span className="font-bold text-slate-700 dark:text-content tabular-nums">{formatEuro(r.cost)}</span>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between pt-2 text-sm">
                                            <span className="font-black text-slate-800 dark:text-content">Total semaine</span>
                                            <span className="font-black text-brand-600 dark:text-brand-400 tabular-nums">{formatEuro(weeklyCostTotal)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div ref={planningTableRef} className="bg-white dark:bg-[var(--bg-card)] rounded-2xl shadow-sm border border-slate-200 dark:border-border overflow-x-auto">
                    {/* table-fixed : toutes les colonnes de jours gardent la MEME largeur, quel que soit leur
                        contenu. Sans lui, un extra au libelle long (« Agent de securite plage ») elargissait sa
                        seule colonne et decalait toute la grille. La largeur minimale est calculee sur le nombre
                        de jours affiches (1 en vue JOUR, 7 en vue semaine) pour garantir 120px par jour avant
                        que le defilement horizontal ne prenne le relais. */}
                    <table
                        className="w-full text-sm border-collapse table-fixed"
                        style={{ minWidth: 176 + displayedDates.length * 120 }}
                    >
                        <thead>
                            <tr>
                                <th className="sticky left-0 z-40 bg-surface-soft border-b border-r border-border p-3 text-left w-[176px] text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">Salarié</th>
                                {displayedDates.map((d, i) => {
                                    const originalIndex = weekStartDates.indexOf(d);
                                    const kpi = getKPIs(d);
                                    return (
                                        <th key={d} className="border-b border-r border-slate-200 dark:border-border bg-slate-50 dark:bg-[var(--bg-soft)] p-2 text-center relative group">
                                            <div className="flex justify-center items-center gap-1">
                                                <span className="font-bold text-slate-700 dark:text-slate-200">{DAYS[originalIndex]}</span>
                                                <span className="text-xs text-slate-400 font-normal">{format(parseISO(d), 'dd/MM')}</span>
                                                <button onClick={() => setOpenDayMenu(openDayMenu === d ? null : d)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded absolute right-1 top-2"><MoreHorizontal size={14} /></button>
                                            </div>
                                            <div className="flex justify-center gap-3 text-[11px] mt-1 font-medium">
                                                <span className="text-orange-600">{kpi.midi} m</span>
                                                <span className="text-indigo-600">{kpi.soir} s</span>
                                            </div>

                                            {openDayMenu === d && (
                                                <div className="absolute top-full right-0 mt-1 bg-white dark:bg-[var(--bg-card)] border border-slate-200 dark:border-border shadow-lg rounded-lg py-1 z-50 w-40 text-left font-normal text-sm">
                                                    <button onClick={() => handleCopyDay(d)} className="w-full px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"><Copy size={14} /> Copier jour</button>
                                                    <button onClick={() => handlePasteDay(d)} disabled={!clipboardDay} className="w-full px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 disabled:opacity-50"><Clipboard size={14} /> Coller jour</button>
                                                    <div className="border-t border-slate-100 dark:border-border my-1"></div>
                                                    <button onClick={() => handleClearDay(d)} className="w-full px-4 py-2 hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 flex items-center gap-2"><Trash2 size={14} /> Vider jour</button>
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(groupedRows) && groupedRows.map(({ roleId, rows }) => (
                                <React.Fragment key={roleId}>
                                    <tr className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => toggleRoleCollapse(roleId)}>
                                        <td colSpan={displayedDates.length + 1} className="py-2 px-3 bg-[#a7e0e0] dark:bg-[#2A5A5A] text-[#1f3a4a] dark:text-[#E0FFFF] text-[11.5px] font-bold border-b border-border tracking-wider uppercase sticky left-0 z-20 text-center transition-colors">
                                            <div className="flex items-center justify-center gap-2">
                                                {collapsedRoles.has(roleId) ? <ChevronRight size={15} /> : <ChevronDown size={15} />} {getRoleLabel(roleId)}
                                                <span className="text-[10px] font-normal bg-white/40 dark:bg-slate-200 dark:bg-surface-soft px-2 py-0.5 rounded-full">{rows.length}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    {!collapsedRoles.has(roleId) && rows.map(row => {
                                        const totalMinutes = displayedDates.reduce((acc, d) => acc + calculateShiftDuration(row.shifts[d]), 0);
                                        return (
                                            <tr key={row.employeeId} className="hover:bg-brand-600/10 transition-colors">
                                                <td className="sticky left-0 z-30 bg-surface border-b border-r border-border px-2.5 py-2 w-[176px] max-w-[176px]">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="w-[25px] h-[25px] rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold bg-surface-soft text-content-muted">{row.employeeName.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</span>
                                                        <div className="min-w-0">
                                                            <div className="text-[12.5px] font-semibold text-content truncate leading-tight">{row.employeeName}</div>
                                                            <div className="text-[10.5px] text-content-muted truncate leading-tight tabular-nums">{getRoleLabel(String(row.employeeRole))}{totalMinutes > 0 && <> · {formatDuration(totalMinutes)}</>}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {displayedDates.map(date => {
                                                    const isLocked = isWeekLocked(date) || user?.type === 'employe';
                                                    return (
                                                        <td key={date} className={`border-b border-l border-[#eef0f3] dark:border-border p-1 h-[46px] relative transition-colors ${isLocked ? 'cursor-default bg-surface-soft/60' : 'cursor-pointer hover:bg-[#fafbfc] dark:hover:bg-surface-soft/50'}`} onClick={(e) => {
                                                            if (isLocked) return;
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setEditorPos({ x: rect.left, y: rect.bottom + window.scrollY });
                                                            setEditingShift({
                                                                shift: row.shifts[date] || { date, type: 'repos', serviceType: 'none', segments: [] },
                                                                employeeId: row.employeeId, employeeName: row.employeeName, employeeRole: row.employeeRole, date
                                                            });
                                                        }}>
                                                            <ShiftCell shift={row.shifts[date]} templates={templates} absenceTypes={absenceTypes} />
                                                            {isLocked && <div className="absolute inset-0 bg-slate-100/5 z-10" />}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                    {!collapsedRoles.has(roleId) && (
                                        <tr>
                                            <td className="sticky left-0 z-30 bg-surface-app border-b border-r border-border px-2.5 py-1.5 text-right text-[11px] font-semibold text-content-muted whitespace-nowrap truncate">Total {getRoleLabel(roleId)}</td>
                                            {displayedDates.map((date) => {
                                                const totals = getGroupTotals(rows as PlanningRow[], date);
                                                return (
                                                    <td key={`${roleId}-total-${date}`} className="border-b border-l border-border bg-surface-app px-2 py-1.5 text-center text-[11px] font-semibold text-content-muted tabular-nums">
                                                        {(totals.midi > 0 || totals.soir > 0)
                                                            ? <span><span className={totals.midi > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-slate-300 dark:text-slate-700'}>{totals.midi} m</span><span className="mx-1 text-slate-300 dark:text-slate-700">/</span><span className={totals.soir > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-300 dark:text-slate-700'}>{totals.soir} s</span></span>
                                                            : <span className="text-slate-300 dark:text-slate-700">-</span>
                                                        }
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {/* Extras display below grouped roles */}
                            {(user?.type !== 'employe' && planning.extraShifts && planning.extraShifts.length > 0) && (
                                <>
                                    <tr><td colSpan={displayedDates.length + 1} className="py-2 px-3 bg-[#f4f1fd] dark:bg-purple-900/30 text-[#4c2d8f] dark:text-purple-300 text-[11.5px] font-bold border-b border-border tracking-wider uppercase sticky left-0 z-20 text-center">Renforts (extras)</td></tr>
                                    <tr>
                                        <td className="sticky left-0 z-30 bg-purple-50/30 dark:bg-purple-900/10 border-b border-r dark:border-border p-3"></td>
                                        {displayedDates.map(date => {
                                            const dayExtras = planning.extraShifts.filter(e => e.date === date);
                                            return (
                                                <td key={date} className="border-b border-r dark:border-border p-1 align-top bg-purple-50/10 dark:bg-purple-900/5">
                                                    <div className="space-y-1">
                                                        {dayExtras.map(extra => (
                                                            <div key={extra.id} className="group flex justify-between items-start gap-1 text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200 rounded px-1.5 py-1 transition-colors">
                                                                <div className="flex flex-col min-w-0">
                                                                    <span className="font-bold break-words leading-tight">{extra.count}x {extra.label}</span>
                                                                    <span className="opacity-70">{extra.start}-{extra.end}</span>
                                                                </div>
                                                                <button onClick={() => handleDeleteExtra(extra.id)} className="text-purple-400 dark:text-purple-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1 shrink-0"><Trash2 size={12} /></button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </>
                            )}
                        </tbody>
                        {/* Pied : heures planifiées par jour, sur les lignes affichées (brief Planning §2). */}
                        <tfoot>
                            <tr>
                                <td className="sticky left-0 z-30 bg-surface-soft border-t border-r border-border px-2.5 py-2 text-right text-[10px] font-bold uppercase tracking-[.09em] text-content-muted whitespace-nowrap">Heures par jour</td>
                                {displayedDates.map((date) => {
                                    const minutes = (Array.isArray(groupedRows) ? groupedRows : []).reduce((acc, g) => acc + (g.rows as PlanningRow[]).reduce((a, r) => a + calculateShiftDuration(r.shifts[date]), 0), 0);
                                    return (
                                        <td key={`foot-${date}`} className="border-t border-l border-border bg-surface-soft px-2 py-2 text-center text-[11.5px] font-semibold text-content tabular-nums">
                                            {minutes > 0 ? formatDuration(minutes) : <span className="text-content-muted">–</span>}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <ConfirmModal request={confirmRequest} onClose={() => setConfirmRequest(null)} />

                {editingShift && (
                    <ShiftEditor
                        shift={editingShift.shift}
                        employeeName={editingShift.employeeName}
                        employeeRoleId={editingShift.employeeRole}
                        availableTemplates={templates}
                        onSave={(updated) => handleShiftUpdate(updated)}
                        onClose={() => setEditingShift(null)}
                        position={editorPos}
                        currentDate={editingShift.date}
                        absenceTypes={absenceTypes}
                    />
                )}

                {isAddModalOpen && (
                    <AddShiftModal
                        isOpen={isAddModalOpen}
                        onClose={() => setIsAddModalOpen(false)}
                        employees={dbEmployees.map(e => ({ 
                            id: e.id, 
                            name: `${(e.last_name || '').toUpperCase()} ${e.first_name || ''}`.trim(), 
                            role: e.position 
                        })).sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))}
                        currentDate={format(currentWeekStart, 'yyyy-MM-dd')}
                        weekDates={weekStartDates}
                        extrasPartners={extrasPartners}
                        extraTypes={extraTypes}
                        absenceTypes={absenceTypes}
                        onAdd={handleAddShiftData}
                    />
                )}

                {isSettingsOpen && (
                    <PlanningSettings
                        isOpen={isSettingsOpen}
                        onClose={() => setIsSettingsOpen(false)}
                        employees={dbEmployees}
                        roles={roles}
                        canManageGlobalTypes={user?.type === 'admin'}
                        onDataChanged={handleSettingsDataChanged}
                    />
                )}

                {isImportModalOpen && (
                    <ImportExcelModal
                        isOpen={isImportModalOpen}
                        onClose={() => setIsImportModalOpen(false)}
                        salaries={dbEmployees}
                        codesAbsence={absenceTypes}
                        semainesExistantes={(weeksList || []).map((w: any) => w.week_start)}
                        // Les mêmes types que « Ajouter un renfort » à la main : catalogue Extras Partenaires, puis historiques.
                        typesRenfort={Array.from(new Set([
                            ...extrasPartners.flatMap((p) => p.types.filter((t) => t.scope_planning !== false && t.scope_planning !== 0).map((t) => t.label)),
                            ...extraTypes,
                        ]))}
                        onImporte={() => { loadData(); fetchWeeksList(); }}
                        onOuvrirReglages={() => setIsSettingsOpen(true)}
                    />
                )}

                {isExportModalOpen && (
                    <PlanningExportModal
                        isOpen={isExportModalOpen}
                        onClose={() => setIsExportModalOpen(false)}
                        planning={planning}
                        roles={roles}
                        rolesOrder={rolesOrder}
                        currentWeekStart={currentWeekStart}
                        weekDates={weekStartDates}
                        selectedDayIndex={selectedDayIndex}
                        employees={dbEmployees}
                    />
                )}
            </div>
        </div>
    );
};

// Un salarié d'un client "Prestation" a un planning de rendez-vous, pas d'horaires de shift —
// on route vers la vue dédiée au lieu de la grille de shifts (réservée aux clients "Restauration").
export const PlanningModule = () => {
    const { user } = useAuth();
    if (user?.type === 'employe' && (user as any)?.client_type === 'prestation') {
        return <PrestationEmployeePlanning />;
    }
    return <PlanningModuleShiftGrid />;
};

// --- Sub-Components for Dashboard ---

function DashboardView({ user, weeks, onOpen, onDelete, onDeleteArchive, onNew, isLoading, setIsSettingsOpen, handleOpenArchive }: any) {
    const [search, setSearch] = useState('');

    // --- Archives grouped by Year > Month ---
    const monday = startOfWeek(new Date(), { weekStartsOn: 1 });

    const filteredWeeks = useMemo(() => {
        let list = [...weeks];
        if (search) {
            const s = search.toLowerCase();
            list = list.filter(w => w.id.toLowerCase().includes(s) || (w.status || '').toLowerCase().includes(s) || (w.label || '').toLowerCase().includes(s));
        }
        list.sort((a, b) => b.week_start.localeCompare(a.week_start));
        return list;
    }, [weeks, search]);

    const activeWeeks = useMemo(() => {
        return filteredWeeks.filter(w => parseISO(w.week_start) >= monday);
    }, [filteredWeeks]);

    const archivedWeeks = useMemo(() => {
        return filteredWeeks.filter(w => parseISO(w.week_start) < monday);
    }, [filteredWeeks]);

    // Group archives: { year: { monthKey: week[] } }
    const archivesByYearMonth = useMemo(() => {
        const grouped: Record<string, Record<string, any[]>> = {};
        archivedWeeks.forEach(w => {
            const date = parseISO(w.week_start);
            const year = String(getYear(date));
            const monthKey = format(date, 'yyyy-MM');
            if (!grouped[year]) grouped[year] = {};
            if (!grouped[year][monthKey]) grouped[year][monthKey] = [];
            grouped[year][monthKey].push(w);
        });
        return grouped;
    }, [archivedWeeks]);

    const sortedYears = useMemo(() =>
        Object.keys(archivesByYearMonth).sort((a, b) => Number(b) - Number(a))
    , [archivesByYearMonth]);

    // Open the most recent year by default
    const [openYears, setOpenYears] = useState<Set<string>>(() => {
        const allYears = Object.keys(archivesByYearMonth).sort((a, b) => Number(b) - Number(a));
        return new Set(allYears.length > 0 ? [allYears[0]] : []);
    });
    const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());

    const toggleYear = (year: string) => {
        setOpenYears(prev => {
            const next = new Set(prev);
            if (next.has(year)) next.delete(year); else next.add(year);
            return next;
        });
    };
    const toggleMonth = (monthKey: string) => {
        setOpenMonths(prev => {
            const next = new Set(prev);
            if (next.has(monthKey)) next.delete(monthKey); else next.add(monthKey);
            return next;
        });
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-black text-slate-800 dark:text-content tracking-tight">Planning</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium mt-1">Gérez les plannings hebdomadaires de votre établissement.</p>
                </div>
                <div className="flex items-center gap-3">

                    <button
                        onClick={onNew}
                        className="flex items-center gap-2 px-6 py-3 bg-brand-600 text-brand-fg rounded-2xl font-bold shadow-lg shadow-[#4AA3A2]/20 hover:brightness-95 transition-all active:scale-95"
                    >
                        <Plus size={20} /> Nouvelle semaine
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-[var(--bg-card)] p-4 rounded-2xl border border-slate-200 dark:border-border shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500"><CalendarIcon size={24} /></div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Semaines</p>
                        <p className="text-xl font-black text-slate-800 dark:text-content">{weeks.length}</p>
                    </div>
                </div>
                <div className="bg-white dark:bg-[var(--bg-card)] p-4 rounded-2xl border border-slate-200 dark:border-border shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500"><CheckCircle2 size={24} /></div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Actifs</p>
                        <p className="text-xl font-black text-slate-800 dark:text-content">{weeks.filter((w: any) => parseISO(w.week_start) >= startOfWeek(new Date(), { weekStartsOn: 1 })).length}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-[var(--bg-card)] rounded-3xl border border-slate-200 dark:border-border shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 dark:border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3 bg-slate-50 dark:bg-[var(--bg-soft)] px-4 py-2 rounded-2xl border border-slate-100 dark:border-border w-full md:w-96">
                        <Search size={18} className="text-slate-400" />
                        <input
                            type="text"
                            placeholder="Rechercher une semaine..."
                            className="bg-transparent border-none outline-none text-sm font-medium w-full text-slate-600 dark:text-slate-300 placeholder:text-slate-400"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="p-6">
                    {isLoading ? (
                        <div className="py-20 text-center flex flex-col items-center gap-3">
                            <RefreshCw size={32} className="animate-spin text-slate-300" />
                            <p className="text-slate-400 font-medium">Chargement des plannings...</p>
                        </div>
                    ) : (
                        <div className="space-y-12">
                            {/* === PLANNINGS ACTIFS === */}
                            <section>
                                <div className="flex items-center gap-2 mb-6 ml-2">
                                    <div className="h-4 w-1 rounded-full bg-emerald-500" />
                                    <h2 className="text-lg font-black text-slate-800 dark:text-content tracking-tight">Plannings Actifs</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {activeWeeks.length > 0 ? activeWeeks.map(w => (
                                        <WeekCard key={w.id} week={w} user={user} onOpen={() => onOpen(w.week_start)} onDelete={() => onDelete(w.id)} />
                                    )) : (
                                        <div className="col-span-full py-16 border-2 border-dashed border-slate-200 dark:border-border rounded-[2.5rem] flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 bg-white/50 dark:bg-surface-soft">
                                            <LayoutPanelLeft size={48} className="mb-4 opacity-10" />
                                            <p className="font-bold text-lg">Aucun planning actif</p>
                                            <button onClick={onNew} className="text-sm text-brand-500 font-black underline mt-2 hover:text-[#3d8786]">Créer ma première semaine</button>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* === ARCHIVES : Arborescence Année > Mois === */}
                            {sortedYears.length > 0 && (
                                <section>
                                    <div className="flex items-center gap-2 mb-4 ml-2">
                                        <div className="h-4 w-1 rounded-full bg-slate-400" />
                                        <h2 className="text-lg font-black text-slate-400 tracking-tight">Archives</h2>
                                    </div>
                                    <div className="space-y-2">
                                        {sortedYears.map(year => {
                                            const isYearOpen = openYears.has(year);
                                            const monthsInYear = Object.keys(archivesByYearMonth[year]).sort((a, b) => b.localeCompare(a));
                                            const totalWeeks = Object.values(archivesByYearMonth[year]).reduce((acc, arr) => acc + arr.length, 0);
                                            return (
                                                <div key={year} className="rounded-2xl border border-slate-200 dark:border-border overflow-hidden">
                                                    {/* En-tête Année */}
                                                    <button
                                                        onClick={() => toggleYear(year)}
                                                        className="w-full flex items-center justify-between px-5 py-4 bg-slate-50 dark:bg-[var(--bg-soft)]/60 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            {isYearOpen
                                                                ? <ChevronDown size={18} className="text-slate-400" />
                                                                : <ChevronRight size={18} className="text-slate-400" />
                                                            }
                                                            <span className="text-base font-black text-slate-700 dark:text-slate-200 tracking-tight">{year}</span>
                                                            <span className="text-xs font-bold text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded-full">{totalWeeks} semaine{totalWeeks > 1 ? 's' : ''}</span>
                                                        </div>
                                                    </button>

                                                    {/* Contenu de l'année : mois */}
                                                    {isYearOpen && (
                                                        <div className="divide-y divide-slate-100 dark:divide-white/5">
                                                            {monthsInYear.map(monthKey => {
                                                                const isMonthOpen = openMonths.has(monthKey);
                                                                const monthWeeks = archivesByYearMonth[year][monthKey];
                                                                const monthDate = parseISO(monthKey + '-01');
                                                                const monthLabel = format(monthDate, 'MMMM yyyy', { locale: fr }).toUpperCase();
                                                                return (
                                                                    <div key={monthKey}>
                                                                        {/* En-tête Mois */}
                                                                        <button
                                                                            onClick={() => toggleMonth(monthKey)}
                                                                            className="w-full flex items-center justify-between px-8 py-3 bg-white dark:bg-[var(--bg-card)] hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                                                                        >
                                                                            <div className="flex items-center gap-3">
                                                                                {isMonthOpen
                                                                                    ? <ChevronDown size={15} className="text-slate-300" />
                                                                                    : <ChevronRight size={15} className="text-slate-300" />
                                                                                }
                                                                                <CalendarIcon size={14} className="text-slate-400" />
                                                                                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">{monthLabel}</span>
                                                                                <span className="text-xs font-bold text-slate-300 dark:text-slate-500">{monthWeeks.length} sem.</span>
                                                                            </div>
                                                                        </button>

                                                                        {/* Cards de semaines */}
                                                                        {isMonthOpen && (
                                                                            <div className="px-6 pb-6 pt-3 bg-slate-50/50 dark:bg-[var(--bg-soft)]/30">
                                                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                                                                    {monthWeeks.map((w: any) => (
                                                                                        <WeekCard
                                                                                            key={w.id}
                                                                                            week={w}
                                                                                            user={user}
                                                                                            onOpen={() => onOpen(w.week_start)}
                                                                                            onDelete={() => onDeleteArchive(w.week_start)}
                                                                                            isArchived
                                                                                            handleOpenArchive={handleOpenArchive}
                                                                                        />
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function WeekCard({ week, user, onOpen, onDelete, isArchived, handleOpenArchive }: any) {
    const isAdmin = user?.type === 'admin';
    const date = parseISO(week.week_start);
    const dateEnd = addDays(date, 6);
    const isCurrentWeek = isSameDay(date, startOfWeek(new Date(), { weekStartsOn: 1 }));

    return (
        <div
            onClick={() => isArchived ? handleOpenArchive(week.week_start) : onOpen()}
            className={`group relative bg-white dark:bg-[var(--bg-card)] border-x border-b border-slate-200 dark:border-border rounded-[1.5rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden border-t-4 ${isArchived ? 'border-t-slate-300 dark:border-t-slate-600' : 'border-t-brand-500'}`}
        >
            <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <div className="bg-slate-100 dark:bg-[var(--bg-soft)] px-3 py-1 rounded-lg text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                        <CalendarIcon size={12} /> SEMAINE {getISOWeek(date)}
                    </div>
                    {isCurrentWeek && (
                        <div className="bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded text-[9px] font-black uppercase">En cours</div>
                    )}
                </div>

                <h4 className="text-lg font-black text-slate-800 dark:text-content leading-tight mb-1">{format(date, 'MMMM yyyy', { locale: fr }).toUpperCase()}</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Du {format(date, 'dd', { locale: fr })} au {format(dateEnd, 'dd MMMM', { locale: fr })}</p>

                <div className="mt-6 flex items-center justify-between border-t border-slate-50 dark:border-border pt-4">
                    <div className="flex -space-x-2">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="w-7 h-7 rounded-full bg-slate-100 dark:bg-[var(--bg-soft)] border-2 border-white dark:border-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-400 dark:text-slate-500">
                                <Users size={12} />
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        {(!isArchived || isAdmin) && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                title="Supprimer"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                        <div className={`p-2 rounded-xl transition-all ${isArchived ? 'bg-slate-50 dark:bg-[var(--bg-soft)] text-slate-400 dark:text-slate-500 group-hover:bg-slate-100 dark:group-hover:bg-slate-700' : 'bg-brand-600/10 text-brand-600 dark:text-brand-400 group-hover:bg-brand-600 group-hover:text-brand-fg'}`}>
                            <ChevronRight size={18} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const CheckCircle2 = ({ size, className }: any) => <RefreshCw size={size} className={className} />;

const NewPlanningModal = ({ isOpen, onClose, onCreate }: any) => {
    const [selectedDate, setSelectedDate] = useState<string>('');

    const handleDateChange = (date: string) => {
        if (!date) return;
        const d = parseISO(date);
        const monday = startOfWeek(d, { weekStartsOn: 1 });
        setSelectedDate(format(monday, 'yyyy-MM-dd'));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-white dark:bg-[var(--bg-card)] rounded-[3rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300 border border-border">
                <div className="p-10 text-center">
                    <div className="h-20 w-20 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500 rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <CalendarIcon size={36} />
                    </div>
                    <h2 className="text-3xl font-black text-slate-800 dark:text-content tracking-tight">Nouveau Planning</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-3 font-medium px-4">Choisissez une semaine pour commencer. La date sera automatiquement ajustée au lundi.</p>

                    <div className="mt-10 space-y-4 text-left">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-6">Choisir une date</label>
                        <div className="relative">
                            <input
                                type="date"
                                className="w-full bg-slate-50 dark:bg-[var(--bg-soft)] border-2 border-slate-100 dark:border-border rounded-[1.5rem] px-6 py-5 text-slate-700 dark:text-content font-bold focus:border-brand-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-brand-500/5 outline-none transition-all cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-6 [&::-webkit-calendar-picker-indicator]:w-6 [&::-webkit-calendar-picker-indicator]:h-6 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100 dark:[&::-webkit-calendar-picker-indicator]:invert"
                                value={selectedDate}
                                onClick={(e) => {
                                    try {
                                        if ('showPicker' in HTMLInputElement.prototype) {
                                            (e.target as HTMLInputElement).showPicker();
                                        }
                                    } catch (err) { }
                                }}
                                onChange={(e) => handleDateChange(e.target.value)}
                            />
                        </div>

                        {selectedDate && (
                            <div className="bg-emerald-50/50 dark:bg-emerald-500/5 border border-emerald-100 dark:border-emerald-500/20 rounded-[1.5rem] p-5 flex items-center gap-4 animate-in slide-in-from-top-2 duration-300">
                                <div className="h-12 w-12 bg-surface rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-sm font-black text-lg border border-emerald-50 dark:border-border">
                                    {format(parseISO(selectedDate), 'dd')}
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-emerald-500 dark:text-emerald-400 uppercase tracking-widest">Semaine du lundi</p>
                                    <p className="text-base font-black text-slate-700 dark:text-content">{format(parseISO(selectedDate), 'dd MMMM yyyy', { locale: fr })}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-8 bg-slate-50/50 dark:bg-[var(--bg-soft)]/50 flex gap-4 border-t border-slate-100 dark:border-border">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-4 rounded-2xl font-black text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all uppercase text-xs tracking-widest"
                    >
                        Annuler
                    </button>
                    <button
                        disabled={!selectedDate}
                        onClick={() => onCreate(selectedDate)}
                        className="flex-1 px-6 py-4 rounded-2xl font-black bg-brand-600 text-brand-fg shadow-xl shadow-[#4AA3A2]/30 disabled:opacity-30 disabled:shadow-none hover:brightness-110 active:scale-[0.98] transition-all uppercase text-xs tracking-widest"
                    >
                        Confirmer
                    </button>
                </div>
            </div>
        </div>
    );
};
